import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { TDIcon, Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("editor@techdishes.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "#08090A" }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <TDIcon size={56} />
          <div className="mt-4">
            <Wordmark size="text-2xl" />
          </div>
          <div className="td-eyebrow mt-2" style={{ color: "#71767c" }}>
            AI Newsroom · Editorial Access
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-xl border p-7"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }}
          data-testid="login-form"
        >
          <label className="td-eyebrow block mb-2" style={{ color: "#A7ABB0" }}>
            Email
          </label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#08090A] border rounded-lg px-4 py-3 text-white outline-none focus:border-[#FF8A1F]/60 mb-4"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
            required
          />
          <label className="td-eyebrow block mb-2" style={{ color: "#A7ABB0" }}>
            Password
          </label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full bg-[#08090A] border rounded-lg px-4 py-3 text-white outline-none focus:border-[#FF8A1F]/60"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
            required
          />

          {error && (
            <p data-testid="login-error" className="mt-4 text-sm text-[#f87171]">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 font-display font-semibold text-[#08090A] transition-transform active:scale-95 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FF8A1F, #FFB347)" }}
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <>Enter Newsroom <ArrowRight size={16} /></>}
          </button>

          <div className="mt-5 pt-4 border-t text-center" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <p className="td-eyebrow" style={{ color: "#71767c", fontSize: "0.55rem" }}>
              Editor access · editor@techdishes.com
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
